import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Badge from '../../components/Badge';
import DataTable from '../../DataTable';
import { API_BASE_URL } from '../../constants/config';

/**
 * ダッシュボード - 未紐付けイベントページ
 */
function PendingEvents({
  pendingEvents,
  setPendingEvents,
  pendingEventSort,
  setPendingEventSort,
  pendingEventPage,
  setPendingEventPage,
  pendingEventPageSize,
  setPendingEventPageSize,
  pendingEventColumns,
  setPendingEventColumns,
  defaultPendingEventColumns,
  selectedPendingEventIds,
  setSelectedPendingEventIds,
  companies,
  setPendingEventToResolve,
  setAvailableLocations,
  setSelectedLocationForResolve,
  setShowLocationSelectModal,
  setSelectedCompany,
  setLocationTypes,
  setUnassignedRfids,
  setLocationNodes,
  setWashers,
  setEditingLocation,
  setParentLocation,
  setLocationForm,
  setShowLocationModal,
  handleResolvePendingEvent,
  handleDeletePendingEvent,
  handleDeleteSelectedPendingEvents,
  fetchDashboardData,
  fetchPendingEvents
}) {
  const navigate = useNavigate();

  const formatDateTime = (dt) => {
    if (!dt) return '-';
    return new Date(dt).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
  };
  
  const getPendingReasonText = (reason) => {
    const reasonMap = {
      'sensor_not_assigned': 'センサー未割当',
      'rfid_not_registered': 'RFID未登録',
      'no_rfid_tag': 'RFIDタグ未検出',
      'washer_not_located': '洗浄機未配置',
      'company_not_found': '会社不明'
    };
    return reasonMap[reason] || reason;
  };

  // ツリー構造をフラット化する関数
  const flattenLocationTree = (nodes) => {
    const result = [];
    const traverse = (nodeList) => {
      for (const node of nodeList) {
        result.push(node);
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      }
    };
    traverse(nodes);
    return result;
  };
  
  // ノードIDから親パスを構築する関数
  const buildLocationPath = (locationNodeId, allNodes) => {
    const path = [];
    let currentId = locationNodeId;
    
    while (currentId) {
      const node = allNodes.find(n => n.location_node_id === currentId);
      if (!node) break;
      path.unshift(node.location_name);
      currentId = node.parent_location_node_id;
    }
    
    return path.join(' > ');
  };

  // ソート処理
  const sortedPendingEvents = [...pendingEvents].sort((a, b) => {
    const { column, order } = pendingEventSort;
    let aVal = a[column];
    let bVal = b[column];
    
    // null/undefined処理
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    
    // 数値比較
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return order === 'asc' ? aVal - bVal : bVal - aVal;
    }
    
    // 文字列・日付比較
    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  });

  // ページング処理
  const totalPendingEvents = sortedPendingEvents.length;
  const startIndex = (pendingEventPage - 1) * pendingEventPageSize;
  const endIndex = startIndex + pendingEventPageSize;
  const paginatedPendingEvents = sortedPendingEvents.slice(startIndex, endIndex);
  
  const handleResolve = async (eventId, reason) => {
    console.log('handleResolve called:', eventId, reason);
    
    // 紐付け管理画面への誘導
    if (reason === 'sensor_not_assigned') {
      alert('センサー管理画面でセンサーを洗浄機に割り当ててください。\n管理メニューの「センサー」タブをクリックしてください。');
      navigate('/management/sensors');
    } else if (reason === 'no_rfid_tag') {
      // RFIDタグが読み取れなかった場合、既存のRFID設置先から選択
      const event = pendingEvents.find(e => e.pending_event_id === eventId);
      if (!event) {
        alert('イベント情報が見つかりません');
        return;
      }
      
      // 会社情報を取得
      const company = companies.find(c => c.company_id === event.company_id);
      if (!company) {
        alert('会社情報が見つかりません');
        return;
      }
      
      try {
        // 設置先階層を取得（RFIDが設定されているもののみ）
        const nodesResponse = await axios.get(`${API_BASE_URL}/companies/${company.company_id}/location_nodes`);
        const treeNodes = nodesResponse.data;
        
        // ツリー構造をフラット化
        const allNodes = flattenLocationTree(treeNodes);
        
        const rfidNodes = allNodes.filter(node => node.rfid_tag_id && node.rfid_tag_id.length > 0);
        
        if (rfidNodes.length === 0) {
          alert('RFIDが設定されている設置先がありません。\n先に設置先階層管理でRFIDを設定してください。');
          return;
        }
        
        // 各RFIDノードに親パス情報を追加
        const rfidNodesWithPath = rfidNodes.map(node => ({
          ...node,
          locationPath: buildLocationPath(node.location_node_id, allNodes)
        }));
        
        // モーダルに必要な情報を設定
        setPendingEventToResolve(event);
        setAvailableLocations(rfidNodesWithPath);
        setSelectedLocationForResolve(null);
        setShowLocationSelectModal(true);
      } catch (err) {
        alert(`設置先情報の取得に失敗しました: ${err.response?.data?.detail || err.message}`);
      }
    } else if (reason === 'rfid_not_registered') {
      // 該当のpending eventからRFIDと会社情報を取得
      const event = pendingEvents.find(e => e.pending_event_id === eventId);
      if (!event) {
        alert('イベント情報が見つかりません');
        return;
      }
      
      // 会社情報を取得
      const company = companies.find(c => c.company_id === event.company_id);
      if (!company) {
        alert('会社情報が見つかりません');
        return;
      }
      
      try {
        // 会社を選択
        setSelectedCompany(company);
        
        // 設置先種別マスタを取得
        const typesResponse = await axios.get(`${API_BASE_URL}/companies/${company.company_id}/location_node_types`);
        const types = typesResponse.data;
        setLocationTypes(types);
        
        // 未割り当てRFIDを取得
        const rfidsResponse = await axios.get(`${API_BASE_URL}/companies/${company.company_id}/unassigned-rfids`);
        const rfids = rfidsResponse.data.unassigned_rfids || [];
        setUnassignedRfids(rfids);
        
        // 設置先階層を取得
        const nodesResponse = await axios.get(`${API_BASE_URL}/companies/${company.company_id}/location_nodes`);
        const treeNodes = nodesResponse.data;
        setLocationNodes(treeNodes);
        
        // 洗浄機リストも取得
        const washersResponse = await axios.get(`${API_BASE_URL}/companies/${company.company_id}/washers`);
        setWashers(washersResponse.data);
        
        // RFIDが既に設置先に紐付けられているか確認（ツリーをフラット化して検索）
        const allNodes = flattenLocationTree(treeNodes);
        const existingLocation = allNodes.find(node => node.rfid_tag_id === event.rfid_tag_id);
        if (existingLocation) {
          // 既に紐付けられている場合は、イベントを自動的に解決
          // FastAPIのBody必須バリデーション対策として空オブジェクトを送る
          await axios.post(`${API_BASE_URL}/pending-events/${eventId}/resolve`, {});
          // 未紐付けイベントとダッシュボードデータを再取得
          if (fetchPendingEvents) {
            await fetchPendingEvents();
          } else {
            const eventsResponse = await axios.get(`${API_BASE_URL}/pending-events`, {
              params: { company_id: company.company_id }
            });
            setPendingEvents(eventsResponse.data || []);
          }
          fetchDashboardData();
          alert(`このRFIDは既に「${existingLocation.location_name}」に紐付けられています。\nイベントを自動的に解決しました。`);
          return;
        }
        
        // 設置先階層タブに切り替え
        navigate('/management/locations');
        
        // 新規登録モーダルを開く（RFIDを事前選択）
        // RFID登録可能な種別（has_rfid=1）を初期選択
        const rfidType = types.find(t => t.has_rfid === 1);
        const defaultTypeId = rfidType ? rfidType.location_node_type_id : (types.length > 0 ? types[0].location_node_type_id : '');
        setEditingLocation(null);
        setParentLocation(null); // ルート設置先として登録
        setLocationForm({
          location_node_type_id: defaultTypeId,
          location_code: '',
          location_name: '',
          rfid_tag_id: event.rfid_tag_id || '',
          sort_order: 0,
          is_active: true
        });
        
        // 状態更新が完了してからモーダルを開く
        setTimeout(() => {
          setShowLocationModal(true);
        }, 100);
      } catch (err) {
        alert(`設置先階層情報の取得に失敗しました: ${err.response?.data?.detail || err.message}`);
      }
    } else {
      // 自動解決を試みる
      handleResolvePendingEvent(eventId);
    }
  };

  return (
    <div>
      <h2>未紐付けイベント</h2>
      <p style={{ color: '#718096', marginBottom: '20px' }}>
        センサーまたはRFIDの紐付けが不完全なイベントです。紐付けを完了させるために、該当する管理画面で設定を行ってください。
      </p>
      
      {/* NOTE: 将来的な権限管理実装時の要件
           - スーパーアドミン: 全ての未紐付けイベントを表示
           - 一般ユーザー: 自社（company_id）の未紐付けイベントのみ表示 */}
      
      {pendingEvents.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <h3>未紐付けイベントはありません</h3>
          <p>すべてのイベントが正しく紐付けられています。</p>
        </div>
      ) : (
        <>
          <DataTable
            columns={pendingEventColumns}
            data={paginatedPendingEvents}
            defaultColumns={defaultPendingEventColumns}
            onColumnsChange={setPendingEventColumns}
            localStorageKey="pendingEventColumns"
            sortConfig={pendingEventSort}
            onSortChange={(config) => setPendingEventSort(config)}
            selectedIds={selectedPendingEventIds}
            onSelectionChange={setSelectedPendingEventIds}
            idField="pending_event_id"
            currentPage={pendingEventPage}
            pageSize={pendingEventPageSize}
            totalItems={totalPendingEvents}
            onPageChange={setPendingEventPage}
            onPageSizeChange={setPendingEventPageSize}
            bulkActionButton={
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <button
                  className="btn btn-danger"
                  onClick={handleDeleteSelectedPendingEvents}
                  disabled={selectedPendingEventIds.length === 0}
                  style={{
                    backgroundColor: selectedPendingEventIds.length === 0 ? '#cbd5e0' : '#f56565',
                    cursor: selectedPendingEventIds.length === 0 ? 'not-allowed' : 'pointer',
                    opacity: selectedPendingEventIds.length === 0 ? 0.6 : 1
                  }}
                >
                  選択した項目を削除 ({selectedPendingEventIds.length})
                </button>
                {selectedPendingEventIds.length > 0 && (
                  <span style={{ color: '#718096', fontSize: '14px' }}>
                    {selectedPendingEventIds.length}件選択中
                  </span>
                )}
              </div>
            }
            emptyMessage="データがありません"
            renderCell={(row, column, cellStyle) => {
              switch (column.id) {
                case 'pending_event_id':
                  return <td key={column.id} style={cellStyle}>{row.pending_event_id}</td>;
                case 'event_timestamp':
                  return <td key={column.id} style={cellStyle}>{formatDateTime(row.event_timestamp)}</td>;
                case 'sensor_name':
                  return (
                    <td key={column.id} style={cellStyle}>
                      {row.sensor_name || row.mac_address}
                      <div style={{fontSize: '0.85em', color: '#718096'}}>ID: {row.sensor_id}</div>
                    </td>
                  );
                case 'rfid_tag_id':
                  return (
                    <td key={column.id} style={{...cellStyle, fontFamily: 'monospace', fontSize: '1.15em'}}>
                      {row.rfid_tag_id || '-'}
                    </td>
                  );
                case 'event_type':
                  return <td key={column.id} style={cellStyle}>{row.end_time ? '洗浄完了' : '洗浄開始'}</td>;
                case 'status':
                  return (
                    <td key={column.id} style={cellStyle}>
                      {row.event_type === 'start' && !row.end_time ? (
                        <Badge text="洗浄中" kind="info" />
                      ) : row.end_time ? (
                        <>
                          <Badge text="完了" kind="good" />
                          <div style={{fontSize: '0.85em', color: '#718096'}}>
                            {formatDateTime(row.end_time)}
                            {row.duration_seconds && ` (${row.duration_seconds}秒)`}
                          </div>
                        </>
                      ) : (
                        '-'
                      )}
                    </td>
                  );
                case 'pending_reason':
                  return <td key={column.id} style={cellStyle}><Badge text={getPendingReasonText(row.pending_reason)} kind="warn" /></td>;
                case 'company_name':
                  return <td key={column.id} style={cellStyle}>{row.company_name || '-'}</td>;
                case 'washer_name':
                  return <td key={column.id} style={cellStyle}>{row.washer_name || '-'}</td>;
                case 'location_name':
                  return <td key={column.id} style={cellStyle}>{row.location_name || '-'}</td>;
                case 'actions':
                  return (
                    <td key={column.id} style={cellStyle}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button 
                          className="btn btn-sm btn-primary"
                          onClick={(e) => {
                            console.log('Button clicked!', e);
                            handleResolve(row.pending_event_id, row.pending_reason);
                          }}
                        >
                          紐付け
                        </button>
                        <button 
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeletePendingEvent(row.pending_event_id)}
                        >
                          削除
                        </button>
                      </div>
                    </td>
                  );
                default:
                  return undefined;
              }
            }}
          />
        </>
      )}
    </div>
  );
}

export default PendingEvents;
